import React, { useCallback, useMemo, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import axiosInstance from './axiosInstance';
import {
  Alert,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Box,
  Button,
  CircularProgress,
  Grid,
  Paper,
  Snackbar,
  Typography,
  Divider,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';

const humanizeKey = (key = '') =>
  key
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (char) => char.toUpperCase());

const JobListingsUpload = () => {
  const [selectedFile, setSelectedFile] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [responseData, setResponseData] = useState(null);
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');

  const onDrop = useCallback((acceptedFiles, fileRejections) => {
    if (fileRejections?.length) {
      setErrorMessage('Only .csv files up to the configured size limit are supported.');
      return;
    }

    const file = acceptedFiles[0];
    if (file) {
      setSelectedFile(file);
      setResponseData(null);
    }
  }, []);

  const { getRootProps, getInputProps, isDragActive, fileRejections } = useDropzone({
    accept: { 'text/csv': ['.csv'] },
    maxFiles: 1,
    multiple: false,
    onDrop,
  });

  const summaryEntries = useMemo(() => {
    if (!responseData) return [];

    return Object.entries(responseData).filter(([key, value]) => {
      if (Array.isArray(value)) return false;
      if (value && typeof value === 'object') return false;
      return value !== undefined;
    });
  }, [responseData]);

  const handleUpload = async () => {
    if (!selectedFile) {
      setErrorMessage('Please select a CSV file before uploading.');
      return;
    }

    const formData = new FormData();
    formData.append('file', selectedFile);

    setIsUploading(true);
    setSuccessMessage('');
    setErrorMessage('');
    setResponseData(null);

    try {
      const { data } = await axiosInstance.post('/api/v1/jobs', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      setResponseData(data);
      setSuccessMessage('Jobs Listing uploaded successfully.');
    } catch (error) {
      const apiMessage = error?.response?.data?.message || error?.response?.data?.error;
      setErrorMessage(apiMessage || 'Failed to upload Jobs Listing.');
    } finally {
      setIsUploading(false);
    }
  };

  const handleClear = () => {
    setSelectedFile(null);
    setResponseData(null);
    setSuccessMessage('');
    setErrorMessage('');
  };

  const renderFilePreview = () => {
    if (!selectedFile) {
      return (
        <Typography variant="body2" color="textSecondary">
          Drag and drop a CSV file here, or click to browse. Only one file can be uploaded at a time.
        </Typography>
      );
    }

    return (
      <Box>
        <Typography variant="subtitle1" fontWeight={600}>
          {selectedFile.name}
        </Typography>
        <Typography variant="body2" color="textSecondary">
          {(selectedFile.size / 1024).toFixed(2)} KB
        </Typography>
      </Box>
    );
  };

  return (
    <Box
      sx={{
        maxWidth: 1100,
        mx: 'auto',
        px: '5%',
        py: 4,
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
      }}
    >
      <Typography variant="h4" gutterBottom sx={{ textAlign: 'left', width: '100%' }}>
        Upload Jobs Listing
      </Typography>
      <Typography variant="body1" color="textSecondary" sx={{ mb: 3 }}>
        Upload a (CSV) job lisitings file.
      </Typography>
      
      <Box
        {...getRootProps({ className: 'dropzone' })}
        sx={{
          border: '2px dashed',
          borderColor: isDragActive ? 'primary.main' : 'grey.400',
          borderRadius: 2,
          p: 4,
          textAlign: 'center',
          backgroundColor: isDragActive ? 'primary.light' : '#fff',
          cursor: 'pointer',
          width: '100%',
          minHeight: 200,
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          transition: 'background-color 0.2s ease, border-color 0.2s ease',
          '&:hover': {
            backgroundColor: '#f0f6ff',
            borderColor: 'primary.main',
          },
        }}
      >
        <input {...getInputProps()} />
        {renderFilePreview()}
        {fileRejections.length > 0 && (
          <Typography variant="caption" color="error" sx={{ mt: 1, display: 'block' }}>
            Unsupported file type. Please upload a CSV file.
          </Typography>
        )}
      </Box>

      <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', mt: 3 }}>
        <Button
          variant="contained"
          color="primary"
          onClick={handleUpload}
          disabled={!selectedFile || isUploading}
          startIcon={isUploading ? <CircularProgress size={18} /> : null}
        >
          {isUploading ? 'Uploading...' : 'Upload CSV'}
        </Button>
        {selectedFile && (
          <Button variant="text" color="secondary" onClick={handleClear} disabled={isUploading}>
            Clear File
          </Button>
        )}
      </Box>

      {responseData && (
        <Box sx={{ mt: 5 }}>
          <Typography variant="h5" gutterBottom>
            Upload Summary
          </Typography>
          <Grid container spacing={2}>
            {summaryEntries.map(([key, value]) => (
              <Grid item xs={12} sm={6} md={4} key={key}>
                <Paper sx={{ p: 2, height: '100%' }} elevation={1}>
                  <Typography variant="subtitle2" color="textSecondary">
                    {humanizeKey(key)}
                  </Typography>
                  <Typography variant="h6" sx={{ mt: 1 }}>
                    {String(value)}
                  </Typography>
                </Paper>
              </Grid>
            ))}
          </Grid>

          {Array.isArray(responseData.details) && responseData.details.length > 0 && (
            <Box sx={{ mt: 4 }}>
              <Typography variant="h6" gutterBottom>
                Location Details
              </Typography>
              {responseData.details.map((detail, index) => (
                <Accordion key={`${detail.location_key || detail.location || index}-${index}`} defaultExpanded={index === 0}>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
                      <Typography variant="subtitle1" fontWeight={600}>
                        {detail.location || 'Unknown Location'}
                      </Typography>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mt: 0.5 }}>
                        <Typography variant="body2" color="textSecondary">
                          Matched Documents: <strong>{detail.matched_documents ?? '—'}</strong>
                        </Typography>
                        <Typography variant="body2" color="textSecondary">
                          Job Numbers: <strong>{detail.job_numbers?.length ?? 0}</strong>
                        </Typography>
                      </Box>
                    </Box>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                      <Typography variant="body2">
                        Location Key: <strong>{detail.location_key || '—'}</strong>
                      </Typography>
                      {detail.details && typeof detail.details === 'string' && (
                        <Typography variant="body2">Details: {detail.details}</Typography>
                      )}
                      {detail.job_numbers && detail.job_numbers.length > 0 && (
                        <>
                          <Divider sx={{ my: 1 }} />
                          <Typography variant="subtitle2">Job Numbers ({detail.job_numbers.length})</Typography>
                          <Box
                            sx={{
                              maxHeight: 220,
                              overflowY: 'auto',
                              backgroundColor: 'grey.100',
                              p: 2,
                              borderRadius: 1,
                              fontFamily: 'monospace',
                              fontSize: 13,
                            }}
                          >
                            {detail.job_numbers.join(', ')}
                          </Box>
                        </>
                      )}
                    </Box>
                  </AccordionDetails>
                </Accordion>
              ))}
            </Box>
          )}
        </Box>
      )}

      <Snackbar
        open={Boolean(successMessage)}
        autoHideDuration={6000}
        onClose={() => setSuccessMessage('')}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity="success" onClose={() => setSuccessMessage('')} sx={{ width: '100%' }}>
          {successMessage}
        </Alert>
      </Snackbar>

      <Snackbar
        open={Boolean(errorMessage)}
        autoHideDuration={6000}
        onClose={() => setErrorMessage('')}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity="error" onClose={() => setErrorMessage('')} sx={{ width: '100%' }}>
          {errorMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default JobListingsUpload;
